import React from 'react'
import { CalendarIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import { format } from 'date-fns'

interface TimeRange {
  from: Date | null
  to: Date | null
  preset?: string
}

interface TimeRangeSelectorProps {
  value: TimeRange
  onChange: (range: TimeRange) => void
  compareEnabled: boolean
  onCompareToggle: (enabled: boolean) => void
}

const presets = [
  { label: 'Today', value: 'today', days: 0 },
  { label: 'Yesterday', value: 'yesterday', days: 1 },
  { label: 'Last 7 Days', value: 'last7days', days: 7 },
  { label: 'Last 30 Days', value: 'last30days', days: 30 },
  { label: 'Last Quarter', value: 'lastquarter', days: 90 },
]

export const TimeRangeSelector: React.FC<TimeRangeSelectorProps> = ({
  value,
  onChange,
  compareEnabled,
  onCompareToggle
}) => {
  const handlePresetClick = (preset: typeof presets[0]) => {
    const to = new Date()
    let from: Date
    
    if (preset.value === 'today') {
      from = new Date()
      from.setHours(0, 0, 0, 0)
    } else if (preset.value === 'yesterday') {
      from = new Date()
      from.setDate(from.getDate() - 1)
      from.setHours(0, 0, 0, 0)
      to.setDate(to.getDate() - 1)
      to.setHours(23, 59, 59, 999)
    } else {
      from = new Date()
      from.setDate(from.getDate() - preset.days)
      from.setHours(0, 0, 0, 0)
    }
    
    onChange({
      from,
      to,
      preset: preset.value
    })
  }

  const handleCustomRange = (range: { from: Date | undefined, to: Date | undefined }) => {
    onChange({
      from: range.from || null,
      to: range.to || null,
      preset: undefined
    })
  }

  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 p-4 bg-card border rounded-lg">
      {/* Quick Presets */}
      <div className="flex flex-wrap gap-2">
        {presets.map((preset) => (
          <Button
            key={preset.value}
            variant={value.preset === preset.value ? "default" : "outline"}
            size="sm"
            onClick={() => handlePresetClick(preset)}
          >
            {preset.label}
          </Button>
        ))}
      </div>

      {/* Custom Date Range */}
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              "justify-start text-left font-normal",
              !value.from && !value.to && "text-muted-foreground"
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {value.from && value.to ? (
              <>
                {format(value.from, "MMM dd, yyyy")} - {format(value.to, "MMM dd, yyyy")}
              </>
            ) : (
              <span>Custom range</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="range"
            defaultMonth={value.from || undefined}
            selected={{ from: value.from || undefined, to: value.to || undefined }}
            onSelect={handleCustomRange}
            numberOfMonths={2}
            className="p-3 pointer-events-auto"
          />
        </PopoverContent>
      </Popover>

      {/* Compare Toggle */}
      <div className="flex items-center space-x-2 ml-auto">
        <Switch
          id="compare-mode"
          checked={compareEnabled}
          onCheckedChange={onCompareToggle}
        />
        <Label htmlFor="compare-mode" className="text-sm font-medium">
          Compare to previous period
        </Label>
      </div>
    </div>
  )
}